import { useEffect, useState } from 'react';
import Navbar from '../GeneralComponents/Navbar';
import SideNav from '../GeneralComponents/SideNav';
import * as Yup from 'yup';
import { Formik, Field, Form } from 'formik';
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai';
import {
  HiOutlineUser,
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineHome,
  HiOutlineCalendar,
  HiOutlineLocationMarker,
  HiOutlineUsers,
} from 'react-icons/hi';
import { toast } from 'react-hot-toast';
import { getServerError } from '../../helper/misc';
import auth from '../../api/auth';
import locations from '../../api/locations';
import BackButton from '../GeneralComponents/BackButton';

const defaults = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  homeAddress: '',
  males: '',
  females: '',
  familySize: '',
  password: '',
  homeType: '',
  confirmPassword: '',
  lga: '',
  state: '',
  dob: '',
};

/* ── Styled sub-components ─────────────────────────────────────── */

function InputField({ label, icon: Icon, error, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider pl-1">
        {label}
      </label>
      <div
        className={`flex items-center gap-3 bg-white border-2 rounded-xl px-4 py-3 transition-all duration-200 focus-within:border-[#109CF1] focus-within:shadow-md focus-within:shadow-[#109CF1]/10 ${
          error ? 'border-red-400' : 'border-gray-200 hover:border-gray-300'
        }`}>
        {Icon && <Icon className="text-gray-400 shrink-0" size={18} />}
        {children}
      </div>
      {error && (
        <p className="text-xs text-red-500 pl-1 mt-0.5">{error}</p>
      )}
    </div>
  );
}

function SectionTitle({ step, title, subtitle }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-pw-blue text-white text-sm font-bold shrink-0">
        {step}
      </span>
      <div>
        <p className="font-semibold text-gray-800 leading-tight">{title}</p>
        {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
      </div>
    </div>
  );
}

/* ── Main Component ─────────────────────────────────────────────── */

function FormUserDetails({ onFormComplete, prevStep, meterNumber }) {
  const [loading, setLoading] = useState(false);
  const [statesList, setStatesList] = useState([]);
  const [lgasList, setLgasList] = useState([]);
  const [formDetails, setFormDetails] = useState({ ...defaults });
  const [toggleEye, setToggleEye] = useState(false);

  const initialValues = {
    firstName: formDetails.firstName,
    lastName: formDetails.lastName,
    email: formDetails.email,
    phoneNumber: formDetails.phoneNumber,
    homeAddress: formDetails.homeAddress,
    males: formDetails.males,
    females: formDetails.females,
    familySize: formDetails.familySize,
    password: formDetails.password,
    meterNumber,
    homeType: formDetails.homeType,
    confirmPassword: formDetails.confirmPassword,
    lga: formDetails.lga,
    state: formDetails.state,
    dob: formDetails.dob,
  };

  const validationSchema = Yup.object({
    firstName: Yup.string().required('Required'),
    lastName: Yup.string().required('Required'),
    email: Yup.string().email('Invalid email').required('Required'),
    phoneNumber: Yup.string().required('Required'),
    homeAddress: Yup.string().required('Required'),
    males: Yup.number().required('Required'),
    females: Yup.number().required('Required'),
    familySize: Yup.number().required('Required'),
    password: Yup.string()
      .required('Required')
      .min(8, 'At least 8 characters.')
      .matches(
        /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,10}$/,
        '8–10 chars, upper, lower, number & special character.',
      ),
    confirmPassword: Yup.string().oneOf(
      [Yup.ref('password'), null],
      'Passwords must match',
    ),
    lga: Yup.number().typeError('Required').required('Required'),
    state: Yup.number().typeError('Required').required('Required'),
    dob: Yup.string().nullable(),
  });

  useEffect(() => {
    locations
      .getStates()
      .then((res) => setStatesList(res.data.data || []))
      .catch((err) => console.error('Failed to fetch states', err));
  }, []);

  useEffect(() => {
    if (formDetails.state) {
      locations
        .getLgas(formDetails.state)
        .then((res) => setLgasList(res.data.data || []))
        .catch((err) => console.error('Failed to fetch LGAs', err));
    } else {
      setLgasList([]);
    }
  }, [formDetails.state]);

  const onSubmit = async () => {
    try {
      if (
        Number(formDetails.familySize) !==
        Number(formDetails.males) + Number(formDetails.females)
      ) {
        toast.error('Family size must equal males + females');
        return;
      }
      setLoading(true);
      const formattedPhone = formDetails.phoneNumber.startsWith('0')
        ? '+234' + formDetails.phoneNumber.slice(1)
        : formDetails.phoneNumber;

      // Request OTP verification email
      await auth.requestVerification({ email: formDetails.email });

      // Read region from meter hierarchy (stored during meter validation)
      const savedHierarchy = JSON.parse(
        sessionStorage.getItem('meter_hierarchy') || '[]',
      );
      const regionId = savedHierarchy[0]?.id || null;

      // Build the signup payload (passed to OTP step for final registration)
      const signupPayload = {
        name: `${formDetails.firstName} ${formDetails.lastName}`,
        email: formDetails.email,
        phone: formattedPhone,
        home_address: formDetails.homeAddress,
        home_type: formDetails.homeType,
        family_size: formDetails.familySize,
        males_in_family: formDetails.males,
        females_in_family: formDetails.females,
        password: formDetails.password,
        meter_number: meterNumber,
        lga: formDetails.lga ? Number(formDetails.lga) : null,
        state: formDetails.state ? Number(formDetails.state) : null,
        region: regionId ? Number(regionId) : null,
        dob: formDetails.dob || null,
      };

      setLoading(false);
      onFormComplete(signupPayload);
    } catch (e) {
      toast.error(getServerError(e));
      setLoading(false);
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormDetails((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === 'state') updated.lga = '';
      return updated;
    });
  };

  const fieldClass =
    'flex-1 bg-transparent border-none outline-none text-sm text-gray-800 placeholder-gray-400 min-h-[28px]';
  const selectClass =
    'flex-1 bg-transparent border-none outline-none text-sm text-gray-800 min-h-[28px] cursor-pointer';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 min-h-screen bg-gray-50">
      <SideNav className="!static" />

      <div className="col-span-1 lg:col-span-3 flex flex-col">
        <Navbar />

        <div className="flex-1 flex flex-col items-center px-4 py-8 lg:py-12">
          {/* ── Page header ── */}
          <div className="w-full max-w-3xl mb-8">
            <div className="flex items-center gap-4">
              <BackButton onClick={prevStep} />
              <div>
                <p className="text-xs font-semibold tracking-widest text-pw-blue uppercase">
                  Start for free
                </p>
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
                  Create your account
                </h1>
              </div>
            </div>

            {/* Progress indicator */}
            <div className="flex items-center gap-2 mt-5 ml-12">
              {['Personal Info', 'Household', 'Location', 'Security'].map(
                (step, i) => (
                  <div key={step} className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-pw-blue" />
                      <span className="text-xs text-gray-500 hidden sm:inline">
                        {step}
                      </span>
                    </div>
                    {i < 3 && (
                      <div className="w-6 h-px bg-gray-300" />
                    )}
                  </div>
                ),
              )}
            </div>
          </div>

          {/* ── Form card ── */}
          <div className="w-full max-w-3xl bg-white rounded-2xl shadow-lg shadow-gray-200/60 border border-gray-100 overflow-hidden">
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}>
              {({ errors, touched }) => (
                <Form onChange={onChange}>

                  {/* Section 1 — Personal Info */}
                  <div className="p-6 lg:p-8 border-b border-gray-100">
                    <SectionTitle
                      step="1"
                      title="Personal Information"
                      subtitle="Your basic details"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <InputField
                        label="First Name"
                        icon={HiOutlineUser}
                        error={touched.firstName && errors.firstName}>
                        <Field
                          name="firstName"
                          id="firstName"
                          placeholder="e.g. Adebayo"
                          autoComplete="given-name"
                          className={fieldClass}
                        />
                      </InputField>

                      <InputField
                        label="Last Name"
                        icon={HiOutlineUser}
                        error={touched.lastName && errors.lastName}>
                        <Field
                          name="lastName"
                          id="lastName"
                          placeholder="e.g. Okafor"
                          autoComplete="family-name"
                          className={fieldClass}
                        />
                      </InputField>

                      <InputField
                        label="Email Address"
                        icon={HiOutlineMail}
                        error={touched.email && errors.email}>
                        <Field
                          type="email"
                          name="email"
                          id="email"
                          placeholder="you@example.com"
                          autoComplete="email"
                          className={fieldClass}
                        />
                      </InputField>

                      <InputField
                        label="Phone Number"
                        icon={HiOutlinePhone}
                        error={touched.phoneNumber && errors.phoneNumber}>
                        <Field
                          name="phoneNumber"
                          id="phoneNumber"
                          placeholder="08012345678"
                          autoComplete="tel"
                          inputMode="tel"
                          className={fieldClass}
                        />
                      </InputField>

                      <InputField
                        label="Date of Birth"
                        icon={HiOutlineCalendar}
                        error={touched.dob && errors.dob}>
                        <Field
                          type="date"
                          name="dob"
                          id="dob"
                          autoComplete="bday"
                          className={`${fieldClass} text-gray-500`}
                        />
                      </InputField>
                    </div>
                  </div>

                  {/* Section 2 — Household */}
                  <div className="p-6 lg:p-8 border-b border-gray-100">
                    <SectionTitle
                      step="2"
                      title="Household Details"
                      subtitle="Tell us about your home"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <InputField
                        label="Home Address"
                        icon={HiOutlineHome}
                        error={touched.homeAddress && errors.homeAddress}>
                        <Field
                          name="homeAddress"
                          id="homeAddress"
                          placeholder="Street address"
                          autoComplete="street-address"
                          className={fieldClass}
                        />
                      </InputField>

                      <InputField
                        label="Home Type"
                        icon={HiOutlineHome}
                        error={touched.homeType && errors.homeType}>
                        <Field
                          name="homeType"
                          id="homeType"
                          placeholder="e.g. Bungalow, Flat"
                          className={fieldClass}
                        />
                      </InputField>

                      <InputField
                        label="Family Size"
                        icon={HiOutlineUsers}
                        error={touched.familySize && errors.familySize}>
                        <Field
                          type="number"
                          name="familySize"
                          id="familySize"
                          placeholder="Total occupants"
                          min="1"
                          className={fieldClass}
                        />
                      </InputField>

                      {/* Males + Females side by side */}
                      <div className="grid grid-cols-2 gap-3">
                        <InputField
                          label="Males"
                          error={touched.males && errors.males}>
                          <Field
                            type="number"
                            name="males"
                            id="males"
                            placeholder="0"
                            min="0"
                            className={fieldClass}
                          />
                        </InputField>
                        <InputField
                          label="Females"
                          error={touched.females && errors.females}>
                          <Field
                            type="number"
                            name="females"
                            id="females"
                            placeholder="0"
                            min="0"
                            className={fieldClass}
                          />
                        </InputField>
                      </div>
                    </div>
                  </div>

                  {/* Section 3 — Location */}
                  <div className="p-6 lg:p-8 border-b border-gray-100">
                    <SectionTitle
                      step="3"
                      title="Location"
                      subtitle="Where are you located?"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <InputField
                        label="State"
                        icon={HiOutlineLocationMarker}
                        error={touched.state && errors.state}>
                        <Field
                          as="select"
                          name="state"
                          id="state"
                          className={selectClass}>
                          <option value="">Select State</option>
                          {statesList.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </Field>
                      </InputField>

                      <InputField
                        label="Local Government Area"
                        icon={HiOutlineLocationMarker}
                        error={touched.lga && errors.lga}>
                        <Field
                          as="select"
                          name="lga"
                          id="lga"
                          disabled={!formDetails.state}
                          className={`${selectClass} disabled:text-gray-300`}>
                          <option value="">
                            {formDetails.state ? 'Select LGA' : 'Select state first'}
                          </option>
                          {lgasList.map((l) => (
                            <option key={l.id} value={l.id}>
                              {l.name}
                            </option>
                          ))}
                        </Field>
                      </InputField>
                    </div>
                  </div>

                  {/* Section 4 — Security */}
                  <div className="p-6 lg:p-8">
                    <SectionTitle
                      step="4"
                      title="Set Your Password"
                      subtitle="At least 8 characters, with upper, lower, number & symbol"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <InputField
                        label="Password"
                        error={touched.password && errors.password}>
                        <Field
                          type={toggleEye ? 'text' : 'password'}
                          name="password"
                          id="password"
                          placeholder="Create password"
                          autoComplete="new-password"
                          className={fieldClass}
                        />
                        <button
                          type="button"
                          onClick={() => setToggleEye((v) => !v)}
                          aria-pressed={toggleEye}
                          aria-label={toggleEye ? 'Hide password' : 'Show password'}
                          className="text-gray-400 hover:text-pw-blue transition-colors shrink-0">
                          {toggleEye ? (
                            <AiFillEyeInvisible size={20} />
                          ) : (
                            <AiFillEye size={20} />
                          )}
                        </button>
                      </InputField>

                      <InputField
                        label="Confirm Password"
                        error={
                          touched.confirmPassword && errors.confirmPassword
                        }>
                        <Field
                          type={toggleEye ? 'text' : 'password'}
                          name="confirmPassword"
                          id="confirmPassword"
                          placeholder="Repeat password"
                          autoComplete="new-password"
                          className={fieldClass}
                        />
                        <button
                          type="button"
                          onClick={() => setToggleEye((v) => !v)}
                          aria-pressed={toggleEye}
                          aria-label={toggleEye ? 'Hide password' : 'Show password'}
                          className="text-gray-400 hover:text-pw-blue transition-colors shrink-0">
                          {toggleEye ? (
                            <AiFillEyeInvisible size={20} />
                          ) : (
                            <AiFillEye size={20} />
                          )}
                        </button>
                      </InputField>
                    </div>

                    {/* Submit */}
                    <div className="mt-8">
                      <button
                        type="submit"
                        disabled={loading}
                        id="create-account-btn"
                        className="w-full sm:w-auto px-10 py-3.5 bg-pw-blue hover:bg-[#0d8fd9] active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-3xl transition-all duration-200 shadow-md shadow-[#109CF1]/30 flex items-center justify-center gap-2">
                        {loading ? (
                          <>
                            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Sending verification…
                          </>
                        ) : (
                          'Continue →'
                        )}
                      </button>
                      <p className="text-xs text-gray-400 mt-3">
                        By creating an account, you agree to our Terms &amp; Privacy Policy.
                      </p>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FormUserDetails;
